import { Howler } from 'howler';
import { settings } from '@/config';
import { createLogger } from '@/utils/logger';

/**
 * SPACE AMBIENCE for story mode (2026-08-18, user: "I want space ambient background effect so
 * the woman's voice feels more immersive, more clear").
 *
 * SYNTHESISED, NOT SAMPLED. A pad that has to sit under narration of unknowable length must loop
 * without a seam and duck on command; a handful of oscillators do exactly that for zero bytes,
 * no loop point to hide, and no extra request on a page that already loads a GLB and nine
 * texture atlases. It is also genuinely endless — nobody can catch it repeating, because it
 * never does.
 *
 * WHAT IT IS, musically: a low root with a fifth and two octaves stacked over it — an open,
 * unresolved chord that creates space without ever asking to resolve, so it never competes with
 * the sentence being spoken. Each partial is a PAIR of oscillators detuned a few cents apart,
 * which beat slowly against each other and produce the wide, drifting quality the ear reads as
 * "vast". Under it sits filtered noise for air, and over the whole thing one very slow LFO
 * sweeps the lowpass so the pad breathes.
 *
 * It shares Howler's `AudioContext` deliberately: one context for the page, already unlocked by
 * the ENTER gate's click, and it moves with the site's own mute state instead of fighting it.
 */
export class StoryAmbience {
  private readonly log = createLogger('ambience');
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private voices: OscillatorNode[] = [];
  private lfo: OscillatorNode | null = null;
  private noise: AudioBufferSourceNode | null = null;
  private running = false;
  /** Ducked while the narrator actually speaks — see {@link setDucked}. */
  private ducked = false;

  /** Build the graph and fade in. Safe to call when already running. */
  start(): void {
    const cfg = settings.storyMode.ambience;
    if (!cfg.enabled || this.running) return;

    const ctx = this.resolveContext();
    if (!ctx) return;
    this.ctx = ctx;
    this.running = true;

    const now = ctx.currentTime;
    this.master = ctx.createGain();
    this.master.gain.setValueAtTime(0.0001, now);
    this.master.connect(ctx.destination);

    // One lowpass for the whole pad — the LFO sweeping it is what makes the bed breathe rather
    // than sit as a static drone.
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(cfg.cutoffHz, now);
    filter.Q.setValueAtTime(0.7, now);
    filter.connect(this.master);

    // A long feedback delay in place of a convolver: no impulse-response file to ship, and at
    // these timescales the ear reads the smeared repeats as room, not as echo.
    const delay = ctx.createDelay(4);
    delay.delayTime.setValueAtTime(1.7, now);
    const feedback = ctx.createGain();
    feedback.gain.setValueAtTime(0.42, now);
    delay.connect(feedback);
    feedback.connect(delay);
    delay.connect(this.master);
    filter.connect(delay);

    // The stacked partials, each a detuned pair.
    for (const partial of cfg.partials) {
      for (const side of [-1, 1]) {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(cfg.rootHz * partial, now);
        osc.detune.setValueAtTime(side * cfg.detuneCents, now);
        const gain = ctx.createGain();
        // Higher partials sit quieter, or the stack turns into an organ rather than a pad.
        gain.gain.setValueAtTime(0.5 / (partial * cfg.partials.length), now);
        osc.connect(gain);
        gain.connect(filter);
        osc.start(now);
        this.voices.push(osc);
      }
    }

    // Air: looping filtered noise, well under the drone.
    this.startNoise(ctx, filter, cfg.airLevel);

    // The breath.
    this.lfo = ctx.createOscillator();
    this.lfo.frequency.setValueAtTime(1 / Math.max(cfg.breathSec, 1), now);
    const lfoGain = ctx.createGain();
    lfoGain.gain.setValueAtTime(cfg.sweepHz, now);
    this.lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);
    this.lfo.start(now);

    this.rampTo(this.targetLevel(), cfg.fadeInSec);
    this.log.info(`space ambience up (${this.voices.length} voices).`);
  }

  /** Fade out and tear the graph down. */
  stop(): void {
    if (!this.running || !this.ctx || !this.master) return;
    const cfg = settings.storyMode.ambience;
    this.running = false;
    this.ducked = false;
    this.rampTo(0.0001, cfg.fadeOutSec);

    // Stop the sources only AFTER the fade, or the tail is cut off with an audible click.
    const stopAt = this.ctx.currentTime + cfg.fadeOutSec + 0.1;
    for (const osc of this.voices) {
      try {
        osc.stop(stopAt);
      } catch {
        /* already stopped */
      }
    }
    try {
      this.lfo?.stop(stopAt);
      this.noise?.stop(stopAt);
    } catch {
      /* already stopped */
    }
    const master = this.master;
    window.setTimeout(() => master.disconnect(), (cfg.fadeOutSec + 0.3) * 1000);
    this.voices = [];
    this.lfo = null;
    this.noise = null;
    this.master = null;
  }

  /**
   * Duck the pad while she speaks, lift it in the gaps. This is what the user means by wanting
   * the voice "more clear": the bed is always there, but it steps back under every sentence
   * instead of competing with it, then returns in the silences between lines.
   */
  setDucked(ducked: boolean): void {
    if (ducked === this.ducked) return;
    this.ducked = ducked;
    if (this.running) this.rampTo(this.targetLevel(), ducked ? 0.6 : 1.6);
  }

  private targetLevel(): number {
    const cfg = settings.storyMode.ambience;
    return Math.max(cfg.volume * (this.ducked ? cfg.duckTo : 1), 0.0001);
  }

  private rampTo(value: number, seconds: number): void {
    if (!this.ctx || !this.master) return;
    const now = this.ctx.currentTime;
    const gain = this.master.gain;
    // `exponentialRampToValueAtTime` from the CURRENT value: exponential ramps track how loudness
    // is actually perceived, so the fade reads as even rather than rushing at one end. It cannot
    // touch zero, hence the 0.0001 floor everywhere.
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(Math.max(gain.value, 0.0001), now);
    gain.exponentialRampToValueAtTime(Math.max(value, 0.0001), now + Math.max(seconds, 0.05));
  }

  /**
   * A few seconds of noise on a loop — the "air" of the space, sitting well under the drone.
   *
   * BROWN noise (an integrated random walk) rather than white: its energy is concentrated low,
   * which the ear reads as distance and room. White noise at the same level just reads as hiss.
   */
  private startNoise(ctx: AudioContext, destination: AudioNode, level: number): void {
    if (level <= 0) return;
    const seconds = 4;
    const buffer = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < data.length; i += 1) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.5;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(level, ctx.currentTime);
    src.connect(gain);
    gain.connect(destination);
    src.start();
    this.noise = src;
  }

  /** Howler owns the page's `AudioContext`; borrow it so there is only ever one. */
  private resolveContext(): AudioContext | null {
    const ctx = (Howler as unknown as { ctx?: AudioContext }).ctx;
    if (ctx) return ctx;
    try {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      return Ctor ? new Ctor() : null;
    } catch {
      this.log.warn('no AudioContext — story ambience disabled.');
      return null;
    }
  }

  dispose(): void {
    this.stop();
  }
}
